/**
 * The app's half of `torik-bim-rooms/v1` — the rooms document the 3D view
 * defines, edits and links to the hub's rooms (card `t_081371a5`; the wire
 * contract is frozen in the hub repo, `src/lib/bimRooms.ts`, and the design is
 * `docs/plans/2026-10-13-bim-rooms.md` §2/§3).
 *
 * The DECISION of what a rooms document means — what parses, where a point
 * falls, how a room is measured, what the "define" probe returns — is pure and
 * testable without a hub, a socket or a Three.js scene. The Three.js half lives
 * in `composables/useBimRoomsLayer.ts`; this file takes the scene's raycast as a
 * plain function.
 *
 * Rules this module keeps (contract §2, plan §6 decisions, all LOCKED):
 *
 *  - **A room is a SUM OF BOXES.** `kind` is exactly `"box"`; a U-shaped
 *    corridor is three sections whose faces may abut. Nothing here merges
 *    outlines — each section is drawn with its own outline on purpose.
 *  - **Model frame, metres, Y up.** `center`/`size` are floor-plane pairs
 *    (`[x, z]` / `[w, d]`), `y0`/`y1` are ABSOLUTE heights, so a half-height box
 *    under a mezzanine is legal and a multi-storey house stays expressible.
 *  - **Strict on read, never fatal.** The hub refuses unknown keys and any
 *    `kind` but `"box"`; this parser mirrors that — a document carrying
 *    something the hub would refuse reads as EMPTY (with a `console.warn`),
 *    never as a half-applied one, and never throws. A view that cannot read the
 *    document must still draw the model.
 *  - **`spots` are round-tripped untouched.** The mini areas (mmWave zones) are
 *    the NEXT card's; this module carries them through verbatim.
 */

export const BIM_ROOMS_SCHEMA = 'torik-bim-rooms/v1';

/** The ceiling a document states when nothing has measured one yet (plan §2). */
export const BIM_ROOMS_DEFAULT_CEILING_HEIGHT_M = 2.7;

/** A room's `name` is capped at 200 characters (the hub's own cap). */
export const MAX_BIM_ROOM_NAME_CHARS = 200;

/** A doc-local id (`room.id`, `section.id`, `spot.id`) is capped at 128 characters. */
export const MAX_BIM_ROOM_KEY_CHARS = 128;

/** The storey list is capped exactly as the hub caps it. */
export const MAX_BIM_ROOM_LEVELS = 64;

/**
 * The narrowest storey the level list may hold (metres) — the cut's own minimum
 * span, so a level's floor and ceiling can never cross on the way to the hub.
 */
export const BIM_LEVEL_MIN_SPAN_M = 0.1;

/**
 * How far ABOVE its own ceiling a storey's device cut may reach when no storey
 * sits above it (metres). A flush-mounted fixture lives in the slab, above the
 * ceiling plane, so the cut has to be allowed past it (owner, 2026-10-19:
 * *"we need to set here e.g. 20 higher than the ceiling and then display those
 * devices in 3d view.. e.g. ceiling lamps flush mounted are above ceiling
 * level"*). A storey with a neighbour above is bounded by that neighbour's floor
 * instead — nothing of the next storey may leak into this one's device view.
 */
export const BIM_DEVICE_CUT_HEADROOM_M = 1;

/** The only colour a room may carry: a six-digit hex triplet, either case. */
export const BIM_ROOM_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * The palette new rooms take their colour from, in order (plan §3.2: "colour
 * auto"). Muted on purpose — the volumes are drawn at ~10 % alpha over a
 * rendered house, so a saturated fill would read as a mistake rather than a
 * room.
 */
export const BIM_ROOM_PALETTE = [
  '#8fae6b',
  '#6b9dae',
  '#ae8f6b',
  '#9d6bae',
  '#6bae9d',
  '#ae6b7d',
  '#7d8fae',
  '#a3ae6b',
] as const;

/**
 * The palette the section cut's storey bands take, in order (owner, 2026-10-18:
 * *"we could also heve colord floor indicators built righ tinto the cut
 * selector"*). A different order from the rooms' own, so a coloured chip beside
 * the cut reads as a storey and never as the room it happens to sit over.
 */
export const BIM_LEVEL_PALETTE = [
  '#6b9dae',
  '#8fae6b',
  '#ae8f6b',
  '#9d6bae',
  '#6bae9d',
  '#ae6b7d',
  '#7d8fae',
  '#a3ae6b',
] as const;

/** A storey's band colour: the palette slot its index falls on. */
export function levelColorOf(index: number): string {
  const count = BIM_LEVEL_PALETTE.length;
  return BIM_LEVEL_PALETTE[((index % count) + count) % count];
}

/** How many rays the "define" probe fans out at eye height (plan §3.2). */
export const BIM_ROOM_PROBE_FAN_RAYS = 72;

/** The extents a probed or hand-typed section is clamped to, in metres. */
export const BIM_ROOM_MIN_EXTENT_M = 0.5;
export const BIM_ROOM_MAX_EXTENT_M = 30;

/** The step every drag and field snaps to (plan §6 decision 3 — 0.1 m). */
export const BIM_ROOM_SNAP_M = 0.1;

/** The height an eye stands at when the probe has to re-cast at floor level. */
export const BIM_ROOM_EYE_HEIGHT_M = 1.6;

/** What a section's footprint falls back to when the fan found no walls at all. */
export const BIM_ROOM_FALLBACK_EXTENT_M = 3;

/** A floor-plane pair: `center [x, z]` or `size [w, d]`, in metres. */
export type BimRoomPair = [number, number];

export interface BimRoomVec3 {
  x: number;
  y: number;
  z: number;
}

export interface BimRoomSection {
  id: string;
  kind: 'box';
  center: BimRoomPair;
  /** `[w, d]` — both strictly positive. */
  size: BimRoomPair;
  /** Facing about the world up axis, degrees (`rotation.y` = this, in radians). */
  yawDeg: number;
  /** Floor height, absolute (metres). */
  y0: number;
  /** Ceiling height, absolute (metres) — always above `y0`. */
  y1: number;
}

/**
 * A mini area inside a room (a cooking zone, an mmWave detection field). This
 * card neither creates nor edits spots — they are carried through untouched for
 * the card that does.
 */
export interface BimRoomSpot {
  id: string;
  name: string;
  kind: 'rect' | 'ellipse';
  center: BimRoomPair;
  size: BimRoomPair;
  yawDeg: number;
  role: 'mmwave-zone' | 'custom' | null;
  zoneIndex: number;
  deviceId: string | null;
}

export interface BimRoom {
  id: string;
  name: string;
  /** The hub room (`rooms.id`) this volume IS, or `null` for an unlinked zone. */
  roomId: string | null;
  color: string | null;
  hidden: boolean;
  sections: BimRoomSection[];
  spots: BimRoomSpot[];
  /**
   * Where the WALK may start inside this room (owner, 2026-10-20: *"for rooms we
   * need entry points.. list.. place on map.."*). A doorway, a terrace door —
   * the place the walker is put when someone steps into the room from the pin.
   * The FACING is not stored: the head is turned toward the room's own centre
   * (`entryLookOf`), so an entry can never disagree with the box it belongs to.
   */
  entries: BimRoomEntry[];
}

/** One entry point of a room: a name and where it stands on the floor. */
export interface BimRoomEntry {
  id: string;
  name: string;
  center: BimRoomPair;
}

export interface BimRoomsDefaults {
  ceilingHeightM: number;
  /** The building's own storeys; an older document without them reads as `[]`. */
  levels: BimRoomLevel[];
}

/** One storey of the building: a label and the three heights it carries. */
export interface BimRoomLevel {
  id: string;
  name: string;
  /** The level the storey's floor sits on, absolute (metres). */
  floorY: number;
  /** The level its ceiling sits on, absolute (metres) — always above `floorY`. */
  ceilingY: number;
  /**
   * The height above which the storey's device PINS are hidden, absolute
   * (metres) — measured DOWN from the ceiling: `ceilingY` hides nothing,
   * `floorY` hides everything in the storey. Within `floorY..ceilingY`.
   */
  deviceCutY: number;
}

export interface BimRoomsDoc {
  schema: typeof BIM_ROOMS_SCHEMA;
  modelId: string;
  defaults: BimRoomsDefaults;
  rooms: BimRoom[];
}

/** What GET answers: the document plus the sidecar's own timestamp. */
export interface BimRoomsPayload extends BimRoomsDoc {
  updatedAt?: string | null;
}

/** The document with no rooms — what a model that was never roomed reads as. */
export function emptyBimRoomsDoc(modelId: string): BimRoomsDoc {
  return {
    schema: BIM_ROOMS_SCHEMA,
    modelId,
    defaults: { ceilingHeightM: BIM_ROOMS_DEFAULT_CEILING_HEIGHT_M, levels: [] },
    rooms: [],
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

const ROOM_KEYS = ['id', 'name', 'roomId', 'color', 'hidden', 'sections', 'spots', 'entries'];
const SECTION_KEYS = ['id', 'kind', 'center', 'size', 'yawDeg', 'y0', 'y1'];
const SPOT_KEYS = ['id', 'name', 'kind', 'center', 'size', 'yawDeg', 'role', 'zoneIndex', 'deviceId'];
const ENTRY_KEYS = ['id', 'name', 'center'];
const LEVEL_KEYS = ['id', 'name', 'floorY', 'ceilingY', 'deviceCutY'];

/** Somewhere to say what was refused — a silent drop is how a document lies. */
function warn(message: string): void {
  console.warn(`[bimRooms] ${message}`);
}

function readKey(raw: unknown, at: string, what = 'an id'): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_BIM_ROOM_KEY_CHARS) {
    warn(`${at} must be ${what} of 1-${MAX_BIM_ROOM_KEY_CHARS} characters`);
    return null;
  }
  return raw;
}

function readPair(raw: unknown, at: string): BimRoomPair | null {
  if (!Array.isArray(raw) || raw.length !== 2 || !isFiniteNumber(raw[0]) || !isFiniteNumber(raw[1])) {
    warn(`${at} must be two finite numbers`);
    return null;
  }
  return [raw[0], raw[1]];
}

function readLink(raw: unknown): string | null {
  if (raw === undefined || raw === null) return null;
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_BIM_ROOM_KEY_CHARS) return null;
  return raw;
}

/** Unknown keys are refused exactly as the hub refuses them (strict, §2). */
function hasUnknownKeys(value: Record<string, unknown>, allowed: readonly string[], at: string): boolean {
  for (const key of Object.keys(value)) {
    if (!allowed.includes(key)) {
      warn(`${at}.${key} is not part of ${BIM_ROOMS_SCHEMA} — the document reads as empty`);
      return true;
    }
  }
  return false;
}

function parseSection(raw: unknown, at: string): BimRoomSection | null {
  if (!isPlainObject(raw) || hasUnknownKeys(raw, SECTION_KEYS, at)) return null;
  if (raw.kind !== 'box') {
    warn(`${at}.kind must be "box" — the only section kind in ${BIM_ROOMS_SCHEMA}`);
    return null;
  }
  const id = readKey(raw.id, `${at}.id`);
  const center = readPair(raw.center, `${at}.center`);
  const size = readPair(raw.size, `${at}.size`);
  if (!id || !center || !size) return null;
  if (size[0] <= 0 || size[1] <= 0) {
    warn(`${at}.size must be [w, d] with both greater than 0`);
    return null;
  }
  const yawDeg = raw.yawDeg === undefined ? 0 : raw.yawDeg;
  if (!isFiniteNumber(yawDeg)) {
    warn(`${at}.yawDeg must be a finite number (degrees)`);
    return null;
  }
  if (!isFiniteNumber(raw.y0) || !isFiniteNumber(raw.y1)) {
    warn(`${at}.y0 and ${at}.y1 must be finite numbers (metres)`);
    return null;
  }
  if (raw.y1 <= raw.y0) {
    warn(`${at}.y1 must be greater than ${at}.y0 (the ceiling is above the floor)`);
    return null;
  }
  return { id, kind: 'box', center, size, yawDeg, y0: raw.y0, y1: raw.y1 };
}

/**
 * A spot is read with the same strictness but dropped individually rather than
 * failing the whole document: the app never edits one, and a spot the hub would
 * refuse must not cost the operator every room on the model.
 */
function parseSpot(raw: unknown, at: string): BimRoomSpot | null {
  if (!isPlainObject(raw) || hasUnknownKeys(raw, SPOT_KEYS, at)) return null;
  if (raw.kind !== 'rect' && raw.kind !== 'ellipse') {
    warn(`${at}.kind must be "rect" or "ellipse"`);
    return null;
  }
  const id = readKey(raw.id, `${at}.id`);
  const center = readPair(raw.center, `${at}.center`);
  const size = readPair(raw.size, `${at}.size`);
  if (!id || !center || !size) return null;
  const name = typeof raw.name === 'string' ? raw.name : '';
  const yawDeg = raw.yawDeg === undefined ? 0 : raw.yawDeg;
  if (!isFiniteNumber(yawDeg)) return null;
  const role = raw.role === 'mmwave-zone' || raw.role === 'custom' ? raw.role : null;
  const zoneIndex = Number.isInteger(raw.zoneIndex) && (raw.zoneIndex as number) >= 0 ? (raw.zoneIndex as number) : 0;
  return { id, name, kind: raw.kind, center, size, yawDeg, role, zoneIndex, deviceId: readLink(raw.deviceId) };
}

/**
 * An entry point, read with the same strictness but dropped individually — a
 * doorway the hub would refuse must not cost the operator the room's box.
 */
function parseEntry(raw: unknown, at: string): BimRoomEntry | null {
  if (!isPlainObject(raw) || hasUnknownKeys(raw, ENTRY_KEYS, at)) return null;
  const id = readKey(raw.id, `${at}.id`);
  const center = readPair(raw.center, `${at}.center`);
  if (!id || !center) return null;
  const name = typeof raw.name === 'string' ? raw.name.slice(0, MAX_BIM_ROOM_NAME_CHARS) : '';
  return { id, name, center };
}

/**
 * One level, read with the same strictness as the hub — but dropped
 * individually: a storey the hub would refuse must not cost the operator every
 * room on the model.
 */
function parseLevel(raw: unknown, at: string): BimRoomLevel | null {
  if (!isPlainObject(raw) || hasUnknownKeys(raw, LEVEL_KEYS, at)) return null;
  const id = readKey(raw.id, `${at}.id`);
  if (!id) return null;
  if (typeof raw.name !== 'string' || raw.name.length > MAX_BIM_ROOM_NAME_CHARS) {
    warn(`${at}.name must be a string of at most ${MAX_BIM_ROOM_NAME_CHARS} characters`);
    return null;
  }
  if (!isFiniteNumber(raw.floorY) || !isFiniteNumber(raw.ceilingY)) {
    warn(`${at}.floorY and ${at}.ceilingY must be finite numbers (metres)`);
    return null;
  }
  if (raw.ceilingY <= raw.floorY) {
    warn(`${at}.ceilingY must be greater than ${at}.floorY (the ceiling is above the floor)`);
    return null;
  }
  // An older document has no device cut: the ceiling IS "hide nothing".
  const deviceCutY = raw.deviceCutY === undefined ? raw.ceilingY : raw.deviceCutY;
  if (!isFiniteNumber(deviceCutY)) {
    warn(`${at}.deviceCutY must be a finite number (metres)`);
    return null;
  }
  // The ceiling is NOT the ceiling of this bound: a flush fixture lives above it.
  // The upper bound is the next storey's floor (see `parseLevels`), or the
  // headroom when this is the top storey.
  if (deviceCutY < raw.floorY) {
    warn(`${at}.deviceCutY must be at or above ${at}.floorY`);
    return null;
  }
  return { id, name: raw.name, floorY: raw.floorY, ceilingY: raw.ceilingY, deviceCutY };
}

/**
 * The highest a storey's device cut may reach: the next storey's floor when one
 * sits above it, else its own ceiling plus `BIM_DEVICE_CUT_HEADROOM_M`. This is
 * the one rule the hub, the parser and the three sliders all read.
 */
export function deviceCutMaxY(level: BimRoomLevel, levels: readonly BimRoomLevel[]): number {
  let next = Number.POSITIVE_INFINITY;
  for (const row of levels) {
    if (!Number.isFinite(row.floorY) || !Number.isFinite(row.ceilingY)) continue;
    if (row.floorY > level.floorY + 1e-6 && row.floorY < next) next = row.floorY;
  }
  return Number.isFinite(next) ? next : level.ceilingY + BIM_DEVICE_CUT_HEADROOM_M;
}

function parseLevels(raw: unknown): BimRoomLevel[] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    warn('defaults.levels must be an array — the level list reads as empty');
    return [];
  }
  const levels: BimRoomLevel[] = [];
  const ids = new Set<string>();
  for (let index = 0; index < raw.length && levels.length < MAX_BIM_ROOM_LEVELS; index += 1) {
    const level = parseLevel(raw[index], `defaults.levels[${index}]`);
    if (!level) continue;
    if (ids.has(level.id)) {
      warn(`defaults.levels[${index}].id "${level.id}" is already used — the duplicate is dropped`);
      continue;
    }
    ids.add(level.id);
    levels.push(level);
  }
  // The device cut may reach into the slab above its ceiling, never into the
  // storey above. Checked once the whole list is in hand, because the bound IS
  // another storey.
  const kept: BimRoomLevel[] = [];
  for (const level of levels) {
    const max = deviceCutMaxY(level, levels);
    if (level.deviceCutY > max + 1e-6) {
      warn(
        `${level.name}: deviceCutY ${level.deviceCutY} reaches past ${max} (the next storey's floor, or the ceiling plus headroom) — the storey is dropped`,
      );
      continue;
    }
    kept.push(level);
  }
  return kept;
}

function parseRoom(raw: unknown, at: string): BimRoom | null {
  if (!isPlainObject(raw) || hasUnknownKeys(raw, ROOM_KEYS, at)) return null;
  const id = readKey(raw.id, `${at}.id`);
  if (!id) return null;
  const name = typeof raw.name === 'string' && raw.name.length <= MAX_BIM_ROOM_NAME_CHARS ? raw.name : null;
  if (name === null) {
    warn(`${at}.name must be a string of at most ${MAX_BIM_ROOM_NAME_CHARS} characters`);
    return null;
  }
  const color = raw.color === undefined || raw.color === null ? null : raw.color;
  if (color !== null && (typeof color !== 'string' || !BIM_ROOM_COLOR_PATTERN.test(color))) {
    warn(`${at}.color must be "#rrggbb" or null`);
    return null;
  }
  if (raw.hidden !== undefined && typeof raw.hidden !== 'boolean') {
    warn(`${at}.hidden must be a boolean`);
    return null;
  }
  if (!Array.isArray(raw.sections) || !Array.isArray(raw.spots)) {
    warn(`${at}.sections and ${at}.spots must be arrays`);
    return null;
  }
  const sections: BimRoomSection[] = [];
  const sectionIds = new Set<string>();
  for (let index = 0; index < raw.sections.length; index += 1) {
    const section = parseSection(raw.sections[index], `${at}.sections[${index}]`);
    if (!section) return null;
    if (sectionIds.has(section.id)) {
      warn(`${at}.sections[${index}].id "${section.id}" is already used — section ids are unique within a room`);
      return null;
    }
    sectionIds.add(section.id);
    sections.push(section);
  }
  const spots: BimRoomSpot[] = [];
  const spotIds = new Set<string>();
  for (let index = 0; index < raw.spots.length; index += 1) {
    const spot = parseSpot(raw.spots[index], `${at}.spots[${index}]`);
    if (!spot || spotIds.has(spot.id)) continue;
    spotIds.add(spot.id);
    spots.push(spot);
  }
  // An older document has no entries: the room reads with none, which is what
  // it had. A document whose `entries` is not a list gives none either.
  const entries: BimRoomEntry[] = [];
  if (Array.isArray(raw.entries)) {
    const entryIds = new Set<string>();
    for (let index = 0; index < raw.entries.length; index += 1) {
      const entry = parseEntry(raw.entries[index], `${at}.entries[${index}]`);
      if (!entry || entryIds.has(entry.id)) continue;
      entryIds.add(entry.id);
      entries.push(entry);
    }
  }
  return {
    id,
    name,
    roomId: readLink(raw.roomId),
    color,
    hidden: raw.hidden === true,
    sections,
    spots,
    entries,
  };
}

/**
 * Read a GET (or PUT) response body into the document the app will honour.
 *
 * Total by contract: a payload that is not an object, a `defaults` block the
 * hub would refuse, an unknown key or a section kind other than `"box"` reads as
 * the EMPTY document (with a warning naming what was refused) rather than as a
 * half-applied one — the view must never draw a guess, and it must never throw.
 */
export function parseBimRoomsPayload(payload: unknown, modelId: string): BimRoomsDoc {
  const empty = emptyBimRoomsDoc(modelId);
  if (!isPlainObject(payload)) {
    warn('the payload is not an object — the document reads as empty');
    return empty;
  }
  if (payload.schema !== undefined && payload.schema !== BIM_ROOMS_SCHEMA) {
    warn(`schema must be "${BIM_ROOMS_SCHEMA}" — the document reads as empty`);
    return empty;
  }
  const defaultsRaw = payload.defaults;
  let ceilingHeightM = BIM_ROOMS_DEFAULT_CEILING_HEIGHT_M;
  const levels = isPlainObject(defaultsRaw) ? parseLevels(defaultsRaw.levels) : [];
  if (isPlainObject(defaultsRaw) && isFiniteNumber(defaultsRaw.ceilingHeightM) && defaultsRaw.ceilingHeightM > 0) {
    ceilingHeightM = defaultsRaw.ceilingHeightM;
  } else {
    warn('defaults.ceilingHeightM is missing or not a positive number — the default ceiling is used');
  }
  if (!Array.isArray(payload.rooms)) {
    warn('"rooms" must be an array — the document reads as empty');
    return { ...empty, defaults: { ceilingHeightM, levels } };
  }
  const rooms: BimRoom[] = [];
  const roomIds = new Set<string>();
  for (let index = 0; index < payload.rooms.length; index += 1) {
    const room = parseRoom(payload.rooms[index], `rooms[${index}]`);
    if (!room) return { ...empty, defaults: { ceilingHeightM, levels } };
    if (roomIds.has(room.id)) {
      warn(`rooms[${index}].id "${room.id}" is already used — room ids are unique in the document`);
      return { ...empty, defaults: { ceilingHeightM, levels } };
    }
    roomIds.add(room.id);
    rooms.push(room);
  }
  return { schema: BIM_ROOMS_SCHEMA, modelId, defaults: { ceilingHeightM, levels }, rooms };
}

/** The body a PUT sends: the document as the hub stores it. */
export function buildBimRoomsBody(doc: BimRoomsDoc): BimRoomsDoc {
  return {
    schema: BIM_ROOMS_SCHEMA,
    modelId: doc.modelId,
    defaults: {
      ceilingHeightM: doc.defaults.ceilingHeightM,
      levels: cloneLevels(doc.defaults.levels),
    },
    rooms: cloneRooms(doc.rooms),
  };
}

export function cloneSection(section: BimRoomSection): BimRoomSection {
  return {
    id: section.id,
    kind: 'box',
    center: [section.center[0], section.center[1]],
    size: [section.size[0], section.size[1]],
    yawDeg: section.yawDeg,
    y0: section.y0,
    y1: section.y1,
  };
}

export function cloneRoom(room: BimRoom): BimRoom {
  return {
    id: room.id,
    name: room.name,
    roomId: room.roomId,
    color: room.color,
    hidden: room.hidden,
    sections: room.sections.map(cloneSection),
    spots: room.spots.map((spot) => ({
      ...spot,
      center: [...spot.center] as BimRoomPair,
      size: [...spot.size] as BimRoomPair,
    })),
    entries: room.entries.map((entry) => ({ ...entry, center: [...entry.center] as BimRoomPair })),
  };
}

export function cloneRooms(rooms: readonly BimRoom[]): BimRoom[] {
  return rooms.map(cloneRoom);
}

export function cloneLevel(level: BimRoomLevel): BimRoomLevel {
  return {
    id: level.id,
    name: level.name,
    floorY: level.floorY,
    ceilingY: level.ceilingY,
    deviceCutY: level.deviceCutY,
  };
}

export function cloneLevels(levels: readonly BimRoomLevel[]): BimRoomLevel[] {
  return levels.map(cloneLevel);
}

// ─── Levels ──────────────────────────────────────────────────────────────────

/** How close a measured floor must be to a level's span to belong to it. */
const BIM_LEVEL_FLOOR_EPS_M = 0.05;

/**
 * The storey a measured floor belongs to: the level whose span contains it (the
 * narrowest when two overlap), else the nearest level BELOW it. `null` when no
 * level sits at or under the floor — a roof terrace above every storey.
 */
export function levelAtFloor(levels: readonly BimRoomLevel[], floorY: number): BimRoomLevel | null {
  if (!Number.isFinite(floorY) || levels.length === 0) return null;
  // The tolerance needs a float slack of its own: `3.06 - 0.05` is a hair ABOVE
  // `3.01` in binary, so a floor measured exactly on the tolerance boundary would
  // otherwise belong to neither storey (the Gallery's 3.01 m fell to the ground).
  const eps = BIM_LEVEL_FLOOR_EPS_M + 1e-6;
  let containing: BimRoomLevel | null = null;
  let containingFloorGap = Number.POSITIVE_INFINITY;
  let below: BimRoomLevel | null = null;
  for (const level of levels) {
    const inside = floorY >= level.floorY - eps && floorY <= level.ceilingY + eps;
    if (inside) {
      // Two storeys meet at a slab: a floor measured ON that height is the
      // UPPER storey's floor, so the level whose own floor is closest wins.
      const floorGap = Math.abs(level.floorY - floorY);
      const span = level.ceilingY - level.floorY;
      const currentSpan = containing ? containing.ceilingY - containing.floorY : Number.POSITIVE_INFINITY;
      if (
        floorGap < containingFloorGap - 1e-9 ||
        (Math.abs(floorGap - containingFloorGap) <= 1e-9 && span < currentSpan)
      ) {
        containing = level;
        containingFloorGap = floorGap;
      }
    } else if (level.floorY <= floorY + eps && (!below || level.floorY > below.floorY)) {
      below = level;
    }
  }
  return containing ?? below;
}

/** The lowest storey — where a new cube is born before it is fitted. */
export function groundLevel(levels: readonly BimRoomLevel[]): BimRoomLevel | null {
  let ground: BimRoomLevel | null = null;
  for (const level of levels) {
    if (!ground || level.floorY < ground.floorY) ground = level;
  }
  return ground;
}

/**
 * The level whose span IS this cut band (within `eps`), or `null` for a custom
 * band. One rule for the cut selector, the top-bar dropdown and the settings
 * rows, so the three can never disagree about which storey is active.
 */
export function levelBySpan(
  levels: readonly BimRoomLevel[],
  bottomY: number,
  topY: number,
  eps = 0.02,
): BimRoomLevel | null {
  if (!Number.isFinite(bottomY) || !Number.isFinite(topY)) return null;
  for (const level of levels) {
    if (Math.abs(level.floorY - bottomY) <= eps && Math.abs(level.ceilingY - topY) <= eps) return level;
  }
  return null;
}

/**
 * Apply a settings edit to a level and keep the span's invariants: the ceiling
 * stays `BIM_LEVEL_MIN_SPAN_M` above the floor, and the device cut stays inside
 * `floorY..maxY` — where `maxY` is the next storey's floor (or the ceiling plus
 * headroom for the top storey). The cut is an ABSOLUTE height now, so a ceiling
 * edit does not carry it along: the operator set it, and it is only clamped back
 * into range when the storey moves out from under it.
 */
export function clampLevelCut(
  level: BimRoomLevel,
  patch: Partial<Pick<BimRoomLevel, 'name' | 'floorY' | 'ceilingY' | 'deviceCutY'>>,
  maxY = level.ceilingY + BIM_DEVICE_CUT_HEADROOM_M,
): BimRoomLevel {
  const askedFloor = Number.isFinite(patch.floorY) ? (patch.floorY as number) : level.floorY;
  const askedCeiling = Number.isFinite(patch.ceilingY) ? (patch.ceilingY as number) : level.ceilingY;
  const floorY = Math.min(askedFloor, askedCeiling - BIM_LEVEL_MIN_SPAN_M);
  const ceilingY = Math.max(askedCeiling, floorY + BIM_LEVEL_MIN_SPAN_M);
  const askedCut = Number.isFinite(patch.deviceCutY) ? (patch.deviceCutY as number) : level.deviceCutY;
  const deviceCutY = Math.min(Math.max(askedCut, floorY), Math.max(maxY, floorY));
  return {
    id: level.id,
    name: patch.name ?? level.name,
    floorY,
    ceilingY,
    deviceCutY,
  };
}

/**
 * Whether a device pin at `y3d` is above the cut that applies to it. `null`/
 * non-finite cut, or a non-finite anchor, never hides (a broken number must not
 * make a pin vanish); a pin exactly ON the cut stays visible.
 */
export function deviceCutHidesPin(y3d: number, deviceCutY: number | null): boolean {
  if (deviceCutY === null || !Number.isFinite(deviceCutY)) return false;
  if (!Number.isFinite(y3d)) return false;
  return y3d > deviceCutY + 1e-6;
}

/** The next storey's span: above the highest ceiling, one default storey tall. */
export function nextLevelPlacement(
  levels: readonly BimRoomLevel[],
  ceilingHeightM: number,
): { floorY: number; ceilingY: number } {
  const height =
    Number.isFinite(ceilingHeightM) && ceilingHeightM > 0.1 ? ceilingHeightM : BIM_ROOMS_DEFAULT_CEILING_HEIGHT_M;
  let top = 0;
  for (const level of levels) top = Math.max(top, level.ceilingY);
  const settle = (value: number): number => Math.round(value * 1e6) / 1e6;
  return { floorY: settle(top), ceilingY: settle(top + height) };
}

/** A fresh level id no level in the list uses. */
export function nextLevelId(levels: readonly BimRoomLevel[], random: () => number = Math.random): string {
  for (let attempt = 0; attempt < 16; attempt += 1) {
    const id = `l-${Date.now().toString(36)}-${randomSuffix(random)}`;
    if (!levels.some((level) => level.id === id)) return id;
  }
  return `l-${Date.now().toString(36)}-${levels.length + 1}`;
}

/** A room's colour: its own, else the palette slot its index falls on. */
export function roomColorOf(room: BimRoom, index = 0): string {
  if (room.color && BIM_ROOM_COLOR_PATTERN.test(room.color)) return room.color;
  return BIM_ROOM_PALETTE[index % BIM_ROOM_PALETTE.length];
}

// ─── Ids ─────────────────────────────────────────────────────────────────────

function randomSuffix(random: () => number): string {
  return Math.floor(random() * 0xffffffff).toString(36);
}

/** A fresh room id no room in the document uses. */
export function nextRoomId(rooms: readonly BimRoom[], random: () => number = Math.random): string {
  for (let attempt = 0; attempt < 16; attempt += 1) {
    const id = `r-${Date.now().toString(36)}-${randomSuffix(random)}`;
    if (!rooms.some((room) => room.id === id)) return id;
  }
  return `r-${Date.now().toString(36)}-${rooms.length + 1}`;
}

/** A fresh section id no section of the room uses. */
export function nextSectionId(room: BimRoom, random: () => number = Math.random): string {
  for (let attempt = 0; attempt < 16; attempt += 1) {
    const id = `s-${Date.now().toString(36)}-${randomSuffix(random)}`;
    if (!room.sections.some((section) => section.id === id)) return id;
  }
  return `s-${room.id}-${room.sections.length + 1}`;
}

// ─── Geometry ────────────────────────────────────────────────────────────────

/** The section's local axes in world coordinates (`rotation.y` = yawDeg). */
export function sectionAxes(section: BimRoomSection): { u: BimRoomVec3; v: BimRoomVec3 } {
  const yaw = (section.yawDeg * Math.PI) / 180;
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  return { u: { x: cos, y: 0, z: -sin }, v: { x: sin, y: 0, z: cos } };
}

/**
 * The room's face whose outward normal best matches a horizontal LOOK direction
 * — the wall a "bring into view" squares up to. Every section contributes its
 * own four wall normals, so a room made of differently turned sections still
 * answers with the wall nearest the view.
 *
 * The sign is kept (the best `dot` picks it), which is what keeps the camera on
 * the side it was already looking from. A view with no bearing (straight above
 * the room) reads the first section's `v` axis; a room with no sections has no
 * face at all.
 */
export function roomFaceNormalForView(room: BimRoom, dx: number, dz: number): BimRoomVec3 | null {
  const first = room.sections[0];
  if (!first) return null;
  // Trig turns an exact axis into `-0` or `6e-17`; a face normal is a direction,
  // and that noise is not a part of it a caller comparing axes wants.
  const clean = (axis: BimRoomVec3): BimRoomVec3 => ({
    x: Math.abs(axis.x) < 1e-12 ? 0 : axis.x,
    y: 0,
    z: Math.abs(axis.z) < 1e-12 ? 0 : axis.z,
  });
  const opposite = (axis: BimRoomVec3): BimRoomVec3 => ({ x: -axis.x, y: 0, z: -axis.z });
  const length = Math.hypot(dx, dz);
  if (!(length > 1e-6)) return clean(sectionAxes(first).v);
  const lookX = dx / length;
  const lookZ = dz / length;
  let best = sectionAxes(first).v;
  let bestDot = Number.NEGATIVE_INFINITY;
  for (const section of room.sections) {
    const { u, v } = sectionAxes(section);
    for (const axis of [u, v, opposite(u), opposite(v)]) {
      const dot = axis.x * lookX + axis.z * lookZ;
      if (dot > bestDot) {
        bestDot = dot;
        best = axis;
      }
    }
  }
  return clean(best);
}

/** A point in the section's own floor-plane frame: `u` across `size[0]`, `v` across `size[1]`. */
export function sectionLocalPoint(section: BimRoomSection, x: number, z: number): { u: number; v: number } {
  const { u, v } = sectionAxes(section);
  const dx = x - section.center[0];
  const dz = z - section.center[1];
  return { u: dx * u.x + dz * u.z, v: dx * v.x + dz * v.z };
}

/** Where a local floor-plane point sits in world coordinates. */
export function sectionWorldPoint(section: BimRoomSection, u: number, v: number): { x: number; z: number } {
  const axes = sectionAxes(section);
  return {
    x: section.center[0] + u * axes.u.x + v * axes.v.x,
    z: section.center[1] + u * axes.u.z + v * axes.v.z,
  };
}

/** The section's four floor corners, in order (world x/z). */
export function sectionCorners(section: BimRoomSection): Array<{ x: number; z: number }> {
  const halfW = section.size[0] / 2;
  const halfD = section.size[1] / 2;
  return [
    sectionWorldPoint(section, -halfW, -halfD),
    sectionWorldPoint(section, halfW, -halfD),
    sectionWorldPoint(section, halfW, halfD),
    sectionWorldPoint(section, -halfW, halfD),
  ];
}

/** Is the point inside the section's footprint (yaw-aware, no height test)? */
export function sectionContainsPoint(section: BimRoomSection, x: number, z: number): boolean {
  const local = sectionLocalPoint(section, x, z);
  return Math.abs(local.u) <= section.size[0] / 2 && Math.abs(local.v) <= section.size[1] / 2;
}

/** One section's floor area, m². */
export function roomAreaM2(section: BimRoomSection): number {
  return section.size[0] * section.size[1];
}

/** A room's area: the SUM of its sections (a U-corridor is three boxes). */
export function roomAreaOfRoom(room: BimRoom): number {
  return room.sections.reduce((sum, section) => sum + roomAreaM2(section), 0);
}

/**
 * Is a point inside the room — inside ANY of its sections' footprints, at a
 * height the section actually spans?
 *
 * `pickY` is the height a caller without one supplies (a floor-plan click, a
 * pick with no surface under it); a point that carries its own `y` uses it. A
 * room with no sections contains nothing (an empty room is a name without a
 * volume — the panel's own "give it a box" case).
 */
export function pointInRoom(room: BimRoom, point: { x: number; z: number; y?: number }, pickY = 0): boolean {
  const y = typeof point.y === 'number' && Number.isFinite(point.y) ? point.y : pickY;
  return room.sections.some(
    (section) => y >= section.y0 - 1e-6 && y <= section.y1 + 1e-6 && sectionContainsPoint(section, point.x, point.z),
  );
}

/**
 * Every room whose volume contains the point, SMALLEST first — the pick rule of
 * plan §3.1 ("the room whose volume contains the hit point; smallest box wins;
 * Alt-click cycles stacked rooms"). Hidden rooms are skipped: a room the
 * operator has taken out of the canvas is not pickable in it either.
 */
export function roomsAtPoint(rooms: readonly BimRoom[], point: { x: number; z: number; y?: number }): BimRoom[] {
  return rooms
    .filter((room) => !room.hidden && pointInRoom(room, point))
    .sort((a, b) => roomAreaOfRoom(a) - roomAreaOfRoom(b));
}

/** The single best room for a point (smallest volume wins), or `null`. */
export function roomAtPoint(rooms: readonly BimRoom[], point: { x: number; z: number; y?: number }): BimRoom | null {
  return roomsAtPoint(rooms, point)[0] ?? null;
}

/** A section's own bounding box on the floor plane. */
export function sectionBounds(section: BimRoomSection): { minX: number; maxX: number; minZ: number; maxZ: number } {
  const corners = sectionCorners(section);
  const xs = corners.map((corner) => corner.x);
  const zs = corners.map((corner) => corner.z);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minZ: Math.min(...zs), maxZ: Math.max(...zs) };
}

/** A room's centre — the mean of its sections' centres (its pin's anchor). */
export function roomCenter(room: BimRoom): { x: number; z: number } {
  if (room.sections.length === 0) return { x: 0, z: 0 };
  let x = 0;
  let z = 0;
  for (const section of room.sections) {
    x += section.center[0];
    z += section.center[1];
  }
  return { x: x / room.sections.length, z: z / room.sections.length };
}

/**
 * The highest ceiling the room's boxes reach, absolute (metres) — the height the
 * automatic section cut opens at, so the storey above the room is what goes and
 * the room's own ceiling stays. `null` for a room with no boxes.
 */
export function roomCeilingY(room: BimRoom): number | null {
  if (room.sections.length === 0) return null;
  let ceiling = Number.NEGATIVE_INFINITY;
  for (const section of room.sections) ceiling = Math.max(ceiling, section.y1);
  return Number.isFinite(ceiling) ? ceiling : null;
}

/**
 * The lowest floor the room's boxes stand on, absolute (metres) — the height
 * that decides WHICH storey the room belongs to. `null` for a room with no
 * boxes.
 */
export function roomFloorY(room: BimRoom): number | null {
  if (room.sections.length === 0) return null;
  let floor = Number.POSITIVE_INFINITY;
  for (const section of room.sections) floor = Math.min(floor, section.y0);
  return Number.isFinite(floor) ? floor : null;
}

/**
 * The storey range a room's focus opens (owner, 2026-10-18: *"we must upgrade the
 * auto cut to show our levels.. auto cut would then 'round up' to the nearest
 * ceiling"*).
 *
 * The storey is the one the room STANDS ON — the level whose span contains the
 * room's own floor (`levelAtFloor`) — and the cut is that storey's whole span.
 * Choosing by the room's TOP alone sent a ground-floor room whose box reaches a
 * few centimetres into the slab up to the floor above (owner, 2026-10-19:
 * *"click on bim room pin on level 0 takes me to another level.. level 1.. ??"*).
 *
 * A room TALLER than its storey (a double-height space) still rounds UP to the
 * nearest level ceiling — the owner's original rule — and keeps its own storey's
 * floor; a room no storey carries (no levels at all, or a volume below every
 * storey) opens at its own top with the cut's floor left where it was.
 */
export interface BimRoomStoreyCut {
  /** The storey's floor, absolute (metres) — `null` = leave the cut's floor alone. */
  bottomY: number | null;
  /** The cut's ceiling: the storey's own ceiling, or the room's top rounded up. */
  topY: number;
}

export function roomStoreyCut(room: BimRoom, levels: readonly BimRoomLevel[]): BimRoomStoreyCut | null {
  const own = roomCeilingY(room);
  const ownFloor = roomFloorY(room);
  if (own === null || ownFloor === null) return null;

  const caps = levels.filter((level) => Number.isFinite(level.floorY) && Number.isFinite(level.ceilingY));
  const storey = levelAtFloor(caps, ownFloor);

  // The room ends below the storey above: it IS this storey, however far its box
  // pokes into this storey's slab. (Measuring from the room's own top instead is
  // what sent ground-floor rooms upstairs — the owner's 2026-10-19 report.)
  if (storey && own <= deviceCutMaxY(storey, caps) + BIM_LEVEL_FLOOR_EPS_M) {
    return { bottomY: storey.floorY, topY: storey.ceilingY };
  }

  // It reaches into the storey above — a double-height space: round UP to the
  // nearest ceiling that covers it, on the floor it stands on.
  let rounded: BimRoomLevel | null = null;
  for (const level of caps) {
    if (level.ceilingY < own - BIM_LEVEL_FLOOR_EPS_M) continue;
    if (!rounded || level.ceilingY < rounded.ceilingY) rounded = level;
  }
  if (rounded) return { bottomY: storey?.floorY ?? rounded.floorY, topY: rounded.ceilingY };

  // No ceiling covers it: its own top, on its own storey's floor.
  return { bottomY: storey ? storey.floorY : null, topY: own };
}

/** Round to the 0.1 m step every drag and field snaps to. */
export function snapMetres(value: number, step = BIM_ROOM_SNAP_M): number {
  if (!Number.isFinite(value)) return 0;
  // `23 * 0.1` is `2.3000000000000003`: the step multiplication is settled to
  // the millimetre so a snapped field reads as the number the operator typed.
  return Math.round(Math.round(value / step) * step * 1e6) / 1e6;
}

export function clampExtent(value: number): number {
  if (!Number.isFinite(value)) return BIM_ROOM_MIN_EXTENT_M;
  return Math.min(BIM_ROOM_MAX_EXTENT_M, Math.max(BIM_ROOM_MIN_EXTENT_M, value));
}

// ─── The "define" probe (plan §3.2) ──────────────────────────────────────────

export interface BimRoomProbeHit {
  /** Distance from the ray's origin, metres. */
  distance: number;
  /** The hit point in the model frame. */
  point: BimRoomVec3;
}

/** The scene's own pick, as a plain function: `null` = the ray found nothing. */
export type BimRoomProbeCast = (origin: BimRoomVec3, direction: BimRoomVec3) => BimRoomProbeHit | null;

export interface BimRoomProbeOptions {
  /** The model's plan axis, degrees — rects are snapped to it (0 for the aligned GLB). */
  axisDeg?: number;
  /** The fallback ceiling height, from the document's own `defaults`. */
  ceilingHeightM: number;
  /** The height the fan is cast at (defaults to the eye's own `y`). */
  probeY?: number;
  /** The level the box sits on when the down-ray misses (defaults to the eye's `y`). */
  levelY?: number;
  /** The direction the fallback box stands in front of, when the fan hits nothing. */
  forward?: BimRoomVec3;
  /** Fan resolution; the plan's own 72 rays (5° apart). */
  fanRays?: number;
  /** The extents clamp (0.5–30 m by the locked decision). */
  minExtentM?: number;
  maxExtentM?: number;
  /** How far the fallback box is placed in front of the eye, metres. */
  fallbackOffsetM?: number;
}

export interface BimRoomProbe {
  center: BimRoomPair;
  size: BimRoomPair;
  yawDeg: number;
  y0: number;
  y1: number;
  /** How many of the fan's rays found a wall — the probe's own confidence. */
  fanHits: number;
  floorFrom: 'ray' | 'level';
  ceilingFrom: 'ray' | 'default';
}

/** The direction of the down/up rays. */
const DOWN: BimRoomVec3 = { x: 0, y: -1, z: 0 };
const UP: BimRoomVec3 = { x: 0, y: 1, z: 0 };

/** A ray that starts on the surface it is aimed at would hit it at 0 m. */
const PROBE_MIN_HIT_M = 0.02;

function normalizeForward(forward: BimRoomVec3 | undefined, axisDeg: number): { x: number; z: number } {
  const x = forward?.x ?? Math.cos((axisDeg * Math.PI) / 180);
  const z = forward?.z ?? Math.sin((axisDeg * Math.PI) / 180);
  const length = Math.hypot(x, z);
  if (length < 1e-6) return { x: Math.cos((axisDeg * Math.PI) / 180), z: Math.sin((axisDeg * Math.PI) / 180) };
  return { x: x / length, z: z / length };
}

/**
 * The gravity "define" probe: a horizontal fan of rays at the probe height finds
 * the walls around the spot, and the box is the ring's own extent **in the
 * model's plan axis** — a rotated min-area rectangle with the axis FIXED (the
 * walls of an axis-aligned export are axis-aligned, so the fixed-axis bounding
 * rectangle of the wall ring IS the min-area rectangle, and it is deterministic,
 * which a search over angles is not).
 *
 * Floor and ceiling come from the vertical rays: `y0` = the ray down, else the
 * probe level; `y1` = the ray up, else `y0 + ceilingHeightM`. Extents are
 * clamped to 0.5–30 m. When the fan finds fewer than three walls (an open side,
 * or a camera in the sky) the fallback is a square standing in front of the eye
 * — the box is always something the operator can then drag, never a refusal.
 *
 * Pure: the caller supplies the scene's own raycast as `cast`.
 */
export function probeRoomBox(eye: BimRoomVec3, cast: BimRoomProbeCast, options: BimRoomProbeOptions): BimRoomProbe {
  const axisDeg = Number.isFinite(options.axisDeg) ? (options.axisDeg as number) : 0;
  const probeY = Number.isFinite(options.probeY) ? (options.probeY as number) : eye.y;
  const levelY = Number.isFinite(options.levelY) ? (options.levelY as number) : eye.y;
  const rays = Math.max(8, Math.floor(options.fanRays ?? BIM_ROOM_PROBE_FAN_RAYS));
  const minExtent = options.minExtentM ?? BIM_ROOM_MIN_EXTENT_M;
  const maxExtent = options.maxExtentM ?? BIM_ROOM_MAX_EXTENT_M;
  const origin: BimRoomVec3 = { x: eye.x, y: probeY, z: eye.z };

  // The fan. A ray that finds nothing contributes nothing; two walls are enough
  // to size one axis, so the ring is only rejected below three hits (a corner
  // seen from outside, or a probe with no walls at all).
  const ring: Array<{ x: number; z: number }> = [];
  for (let index = 0; index < rays; index += 1) {
    const angle = (index / rays) * Math.PI * 2;
    const direction: BimRoomVec3 = { x: Math.cos(angle), y: 0, z: Math.sin(angle) };
    const hit = cast(origin, direction);
    if (!hit || !Number.isFinite(hit.distance) || hit.distance < PROBE_MIN_HIT_M) continue;
    ring.push({ x: hit.point.x, z: hit.point.z });
  }

  const axis = (axisDeg * Math.PI) / 180;
  const cos = Math.cos(axis);
  const sin = Math.sin(axis);
  let center: BimRoomPair;
  let size: BimRoomPair;
  if (ring.length >= 3) {
    let minU = Number.POSITIVE_INFINITY;
    let maxU = Number.NEGATIVE_INFINITY;
    let minV = Number.POSITIVE_INFINITY;
    let maxV = Number.NEGATIVE_INFINITY;
    for (const point of ring) {
      const u = point.x * cos + point.z * sin;
      const v = -point.x * sin + point.z * cos;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    const centerU = (minU + maxU) / 2;
    const centerV = (minV + maxV) / 2;
    center = [centerU * cos - centerV * sin, centerU * sin + centerV * cos];
    size = [
      Math.min(maxExtent, Math.max(minExtent, maxU - minU)),
      Math.min(maxExtent, Math.max(minExtent, maxV - minV)),
    ];
  } else {
    // No walls found: stand a square in front of the eye, on the same axis.
    const forward = normalizeForward(options.forward, axisDeg);
    const offset = options.fallbackOffsetM ?? BIM_ROOM_FALLBACK_EXTENT_M / 2;
    const extent = clampExtent(BIM_ROOM_FALLBACK_EXTENT_M);
    center = [eye.x + forward.x * offset, eye.z + forward.z * offset];
    size = [extent, extent];
  }

  // Floor and ceiling: the vertical rays, else the level and the stated default.
  const down = cast({ x: center[0], y: probeY, z: center[1] }, DOWN);
  const floorFrom: BimRoomProbe['floorFrom'] = down && down.distance >= PROBE_MIN_HIT_M ? 'ray' : 'level';
  const y0 = floorFrom === 'ray' ? (down as BimRoomProbeHit).point.y : levelY;
  const up = cast({ x: center[0], y: Math.max(probeY, y0), z: center[1] }, UP);
  const ceilingFrom: BimRoomProbe['ceilingFrom'] = up && up.distance >= PROBE_MIN_HIT_M ? 'ray' : 'default';
  const ceiling = ceilingFrom === 'ray' ? (up as BimRoomProbeHit).point.y : y0 + options.ceilingHeightM;
  const y1 = ceiling > y0 ? ceiling : y0 + options.ceilingHeightM;

  // A trig round-trip leaves noise in the last bits (`3.0000000000000004`); the
  // probe's numbers are read by eye in the panel, so they are settled here.
  const settle = (value: number): number => Math.round(value * 1e6) / 1e6;
  return {
    center: [settle(center[0]), settle(center[1])],
    size: [settle(size[0]), settle(size[1])],
    yawDeg: axisDeg,
    y0: settle(y0),
    y1: settle(y1),
    fanHits: ring.length,
    floorFrom,
    ceilingFrom,
  };
}

/** A section from a probe's own numbers, keyed by the caller. */
export function sectionFromProbe(probe: BimRoomProbe, id: string): BimRoomSection {
  return {
    id,
    kind: 'box',
    center: [probe.center[0], probe.center[1]],
    size: [probe.size[0], probe.size[1]],
    yawDeg: probe.yawDeg,
    y0: probe.y0,
    y1: probe.y1,
  };
}

// ─── Snap to wall (plan §3.3) ────────────────────────────────────────────────

export interface BimRoomWallHit {
  /** Where the ray met the wall, in the model frame. */
  point: BimRoomVec3;
  /** The surface's world normal (floored: its Y component is ignored). */
  normal: BimRoomVec3;
}

export interface BimRoomSnapResult {
  center: BimRoomPair;
  /** Which local axis moved — the panel names it, the rig asserts it. */
  axis: 'u' | 'v';
  /** How far the section moved, metres (signed, along that axis). */
  deltaM: number;
}

/**
 * Move the section's NEAREST face plane onto the wall's own plane.
 *
 * The wall is a plane with a world normal; the section's four faces are planes
 * at ±w/2 (local `u`) and ±d/2 (local `v`). The face that moves is the one whose
 * own outward normal agrees with the wall's (`n·wall > 0.5`) and whose plane is
 * closest to the wall's — then the section slides along that face's normal so
 * the two planes coincide, which is the plan's "offset 0 = centre-to-centre
 * face; no air gap". A `gapM` pulls the face back off the wall by that much.
 *
 * `null` when no face looks at the wall (a wall behind the section's own plane
 * orientation — the operator's own cue that they picked the wrong side).
 */
export function snapSectionToWall(
  section: BimRoomSection,
  hit: BimRoomWallHit,
  options: { gapM?: number } = {},
): BimRoomSnapResult | null {
  const wall = { x: hit.normal.x, z: hit.normal.z };
  const wallLength = Math.hypot(wall.x, wall.z);
  if (wallLength < 1e-6) return null;
  const nWall = { x: wall.x / wallLength, z: wall.z / wallLength };
  const axes = sectionAxes(section);
  const half = [section.size[0] / 2, section.size[1] / 2];
  const toHit = { x: hit.point.x - section.center[0], z: hit.point.z - section.center[1] };
  const toHitLength = Math.hypot(toHit.x, toHit.z);
  if (toHitLength < 1e-6) return null;
  const direction = { x: toHit.x / toHitLength, z: toHit.z / toHitLength };
  const faces = [
    { axis: 'u' as const, normal: axes.u, offset: half[0] },
    { axis: 'u' as const, normal: { x: -axes.u.x, y: 0, z: -axes.u.z }, offset: half[0] },
    { axis: 'v' as const, normal: axes.v, offset: half[1] },
    { axis: 'v' as const, normal: { x: -axes.v.x, y: 0, z: -axes.v.z }, offset: half[1] },
  ];
  let best: { axis: 'u' | 'v'; normal: BimRoomVec3; distance: number; facing: number } | null = null;
  for (const face of faces) {
    // The face that moves is the one pointing AT the wall (the nearest face in
    // the hit's own direction) — a face on the far side would drag the box
    // across the room.
    const facing = face.normal.x * direction.x + face.normal.z * direction.z;
    if (facing <= 0) continue;
    // …and it only snaps onto a wall that is PARALLEL to it: a hit on the floor,
    // a diagonal or a doorway jamb is not a plane this face can land on. The bar
    // is ~25°, loose enough for a model that is a couple of degrees off its own
    // axis and tight enough to refuse a 45° jamb.
    const parallel = Math.abs(face.normal.x * nWall.x + face.normal.z * nWall.z);
    if (parallel < 0.9) continue;
    const point = {
      x: section.center[0] + face.normal.x * face.offset,
      z: section.center[1] + face.normal.z * face.offset,
    };
    // How far the face's plane must travel along its own normal to contain the
    // wall's hit point: moving the centre by exactly this puts the two planes
    // together (gap 0 = no air gap).
    const distance = (hit.point.x - point.x) * face.normal.x + (hit.point.z - point.z) * face.normal.z;
    if (!best || facing > best.facing) best = { axis: face.axis, normal: face.normal, distance, facing };
  }
  if (!best) return null;
  const gap = options.gapM ?? 0;
  const move = best.distance - gap;
  return {
    center: [section.center[0] + best.normal.x * move, section.center[1] + best.normal.z * move],
    axis: best.axis,
    deltaM: move,
  };
}

// ─── Hub-room matching (plan §3.2) ───────────────────────────────────────────

export interface BimRoomNameCandidate {
  id: string;
  roomName: string;
}

export interface BimRoomNameMatch {
  id: string;
  how: 'exact' | 'normalized' | 'alias';
}

/**
 * Fold a name for comparison: lowercase, diacritics stripped, everything that is
 * not a letter or a digit removed. `Dnevna soba` and `dnevnasoba` are the same
 * name; `Kuhinja` and `küchen` are not.
 */
export function foldRoomName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * The hub room a defined room should link to, matched the plan's way: exact name
 * first, then the folded name, then any ALIAS the caller knows for that room
 * (the Slovenian name of the same preset — plan §3.2's "fuzzy hub match exact →
 * normalised → SL `roomNameSl`"). `null` = nothing matched, and the room stays
 * unlinked rather than being attached to a guess.
 */
export function matchHubRoom(
  name: string,
  rooms: readonly BimRoomNameCandidate[],
  aliasOf?: (room: BimRoomNameCandidate) => readonly string[],
): BimRoomNameMatch | null {
  const needle = name.trim();
  if (needle.length === 0) return null;
  for (const room of rooms) {
    if (room.roomName === needle) return { id: room.id, how: 'exact' };
  }
  const folded = foldRoomName(needle);
  if (folded.length === 0) return null;
  for (const room of rooms) {
    if (foldRoomName(room.roomName) === folded) return { id: room.id, how: 'normalized' };
  }
  if (aliasOf) {
    for (const room of rooms) {
      const aliases = aliasOf(room) ?? [];
      if (aliases.some((alias) => foldRoomName(alias) === folded)) return { id: room.id, how: 'alias' };
    }
  }
  return null;
}

// ─── The document's own edits ────────────────────────────────────────────────

/** Replace one room's whole record (the hub's own document stays a list of rooms). */
export function upsertRoom(rooms: BimRoom[], room: BimRoom): BimRoom[] {
  const index = rooms.findIndex((row) => row.id === room.id);
  if (index === -1) return [...rooms.map(cloneRoom), cloneRoom(room)];
  return rooms.map((row, at) => (at === index ? cloneRoom(room) : cloneRoom(row)));
}

export function removeRoomFrom(rooms: BimRoom[], roomId: string): BimRoom[] {
  return rooms.filter((room) => room.id !== roomId).map(cloneRoom);
}

/** Add one section to a room (appended — the U-corridor grows a leg). */
export function addSectionToRoom(room: BimRoom, section: BimRoomSection): BimRoom {
  return { ...cloneRoom(room), sections: [...room.sections.map(cloneSection), cloneSection(section)] };
}

export function updateSectionInRoom(room: BimRoom, section: BimRoomSection): BimRoom {
  return {
    ...cloneRoom(room),
    sections: room.sections.map((row) => (row.id === section.id ? cloneSection(section) : cloneSection(row))),
  };
}

/**
 * Drop one section of a room. The last section is NOT removable through this
 * helper — a room with no box has no volume to draw or pick, and the panel
 * deletes the room instead (`canRemoveSection`).
 */
export function removeSectionFromRoom(room: BimRoom, sectionId: string): BimRoom {
  return {
    ...cloneRoom(room),
    sections: room.sections.filter((section) => section.id !== sectionId).map(cloneSection),
  };
}

export function canRemoveSection(room: BimRoom): boolean {
  return room.sections.length > 1;
}

/** What a new room is called before the operator renames it. */
export function defaultRoomName(rooms: readonly BimRoom[]): string {
  let index = rooms.length + 1;
  const taken = new Set(rooms.map((room) => room.name));
  while (taken.has(`Room ${index}`)) index += 1;
  return `Room ${index}`;
}

// ─── The debug hook ──────────────────────────────────────────────────────────

export interface BimRoomsDebugInput {
  modelId: string | null;
  status: string;
  message: string;
  rooms: readonly BimRoom[];
  selectedRoomId: string | null;
  selectedSectionId: string | null;
  layer: { meshes: number; outlines: number; handles: number };
  saving: boolean;
  loaded: boolean;
}

/**
 * What `window.__bimRooms()` answers — the rig's read of the live surface
 * (counts, ids, the areas the panel shows), never the scene's objects.
 */
export function roomsDebugDump(input: BimRoomsDebugInput): Record<string, unknown> {
  return {
    modelId: input.modelId,
    status: input.status,
    message: input.message,
    loaded: input.loaded,
    saving: input.saving,
    count: input.rooms.length,
    selectedRoomId: input.selectedRoomId,
    selectedSectionId: input.selectedSectionId,
    layer: { ...input.layer },
    rooms: input.rooms.map((room) => ({
      id: room.id,
      name: room.name,
      roomId: room.roomId,
      color: room.color,
      hidden: room.hidden,
      areaM2: Math.round(roomAreaOfRoom(room) * 100) / 100,
      sections: room.sections.map((section) => ({
        id: section.id,
        center: [section.center[0], section.center[1]],
        size: [section.size[0], section.size[1]],
        yawDeg: section.yawDeg,
        y0: section.y0,
        y1: section.y1,
      })),
      spots: room.spots.length,
      /**
       * The spots themselves (card t_bca466e2) — the role/zone/device facts a
       * rig asserts on. `spots` stays the COUNT for the readers that were
       * already using it.
       */
      spotRows: room.spots.map((spot) => ({
        id: spot.id,
        name: spot.name,
        kind: spot.kind,
        center: [spot.center[0], spot.center[1]],
        size: [spot.size[0], spot.size[1]],
        yawDeg: spot.yawDeg,
        role: spot.role,
        zoneIndex: spot.zoneIndex,
        deviceId: spot.deviceId,
      })),
      /** The walk's own doors in this room (owner, 2026-10-20). */
      entries: room.entries.map((entry) => ({
        id: entry.id,
        name: entry.name,
        center: [entry.center[0], entry.center[1]],
      })),
    })),
  };
}

/** Number formatting shared by the rooms panel's fields and readouts. */
export function formatMetres(value: number, digits = 2): string {
  return Number.isFinite(value) ? value.toFixed(digits) : '—';
}

export function formatDegrees(value: number, digits = 1): string {
  return Number.isFinite(value) ? `${value.toFixed(digits)}°` : '—';
}
